import type {
  ScaleConnectionType,
  ScaleFormatType,
  ScaleMeasurementMode,
} from '@rematter/frontend-lib-api-client';
import {
  enumScaleConnectionType,
  enumScaleMeasurementMode,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';
import isEqual from 'lodash/isEqual';

import { ScaleDetailRecord } from '../types/sidecar';

export type ScaleFormState = {
  name: string;
  connectionType: ScaleConnectionType;
  scaleFormatType: ScaleFormatType | string;
  measurementMode: ScaleMeasurementMode;
  onDemandString: string;
  unitOfMeasurement: string;
  tcpHost: string;
  tcpPort: string;
  serialPortPath: string;
  baudRate: string;
  dataBits: string;
  stopBits: string;
  parity: string;
  cameraId: string | null;
};

const initialState = (): ScaleFormState => ({
  name: '',
  connectionType: enumScaleConnectionType.TCP,
  scaleFormatType: '',
  measurementMode: enumScaleMeasurementMode.Continuous,
  onDemandString: '',
  unitOfMeasurement: '',
  tcpHost: '',
  tcpPort: '',
  serialPortPath: '',
  baudRate: '9600',
  dataBits: '8',
  stopBits: '1',
  parity: 'None',
  cameraId: null,
});

const mapScaleToForm = (scale: ScaleDetailRecord): ScaleFormState => ({
  name: scale.name,
  connectionType: scale.connectionType as ScaleConnectionType,
  scaleFormatType: scale.scaleFormatType,
  measurementMode: scale.measurementMode as ScaleMeasurementMode,
  onDemandString: scale.onDemandString ?? '',
  unitOfMeasurement: scale.unitOfMeasurement ?? '',
  tcpHost: scale.tcpHost ?? '',
  tcpPort: scale.tcpPort?.toString() ?? '',
  serialPortPath: scale.serialPortPath ?? '',
  baudRate: scale.baudRate?.toString() ?? '9600',
  dataBits: scale.dataBits?.toString() ?? '8',
  stopBits: scale.stopBits ?? '1',
  parity: scale.parity ?? 'None',
  cameraId: scale.cameraId,
});

export const $scaleForm = atom<ScaleFormState>(initialState());
export const $discardScaleBaseline = atom<ScaleFormState | null>(null);
export const $lastPopulatedScaleId = atom<string | undefined>(undefined);
export const $isScaleFormPopulated = atom<boolean>(false);

export const $hasUnsavedScaleChanges = atom((get) => {
  const form = get($scaleForm);
  const baseline = get($discardScaleBaseline);

  if (!baseline) return false;

  return !isEqual(form, baseline);
});

export const $canSubmitScaleForm = atom((get) => {
  const form = get($scaleForm);
  if (!form.name.trim()) return false;
  return true;
});

export const $populateScaleFormFromScale = atom(
  null,
  (_get, set, scale: ScaleDetailRecord) => {
    const formState = mapScaleToForm(scale);
    set($scaleForm, formState);
    set($discardScaleBaseline, formState);
    set($lastPopulatedScaleId, scale.id);
    set($isScaleFormPopulated, true);
  },
);

export const $resetScaleFormToScale = atom(null, (get, set) => {
  const baseline = get($discardScaleBaseline);
  if (baseline) {
    set($scaleForm, baseline);
  }
});

export const $populateScaleFormAction = atom(
  null,
  (get, set, scale: ScaleDetailRecord | null | undefined) => {
    if (!scale) return;

    const currentScaleId = scale.id;
    const lastScaleId = get($lastPopulatedScaleId);

    if (currentScaleId !== lastScaleId) {
      set($isScaleFormPopulated, false);
    }

    if (!get($isScaleFormPopulated)) {
      set($populateScaleFormFromScale, scale);
    }
  },
);

export const $resetScaleForm = atom(null, (_get, set) => {
  set($scaleForm, initialState());
  set($discardScaleBaseline, null);
  set($lastPopulatedScaleId, undefined);
  set($isScaleFormPopulated, false);
});
